import { useState } from "react";
import { useAnalytics, AnalyticsData } from "@/hooks/useAnalytics";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Loader2, Users, Eye, TrendingUp, TrendingDown, Minus, Globe, Monitor, FileText, Radio, Clock, ArrowRight, MapPin, MousePointerClick, LogOut, Navigation, Phone, Search, FileX, BadgeCheck, CalendarDays, Share2, Link2, Bot, MessageCircle, Send, ChevronDown } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, PieChart, Pie, Cell, AreaChart, Area } from "recharts";
import { Tooltip as UITooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";

const ShelterNameTooltip = ({ name, children }: { name: string; children: React.ReactNode }) => (
  <UITooltip delayDuration={150}>
    <TooltipTrigger asChild>{children}</TooltipTrigger>
    <TooltipContent side="top" className="max-w-xs break-words">{name}</TooltipContent>
  </UITooltip>
);


const SOURCE_COLORS = ["hsl(var(--primary))", "hsl(var(--primary) / 0.65)", "hsl(var(--primary) / 0.35)", "hsl(var(--primary) / 0.2)"];

const formatDuration = (seconds: number): string => {
  if (seconds < 60) return `${seconds}s`;
  const min = Math.floor(seconds / 60);
  const sec = seconds % 60;
  return `${min}m ${sec}s`;
};

// ── Section header ──
const SectionHeader = ({ icon: Icon, title }: { icon: React.ElementType; title: string }) => (
  <div className="flex items-center gap-2 pt-2">
    <Icon className="w-4 h-4 text-primary" />
    <h3 className="text-sm font-semibold text-foreground">{title}</h3>
    <div className="flex-1 h-px bg-border/60" />
  </div>
);

// ── Compact summary stat ──
const StatCard = ({
  icon: Icon,
  label,
  value,
  accent,
  sub,
  animDelay,
}: {
  icon: React.ElementType;
  label: string;
  value: string | number;
  accent?: boolean;
  sub?: React.ReactNode;
  animDelay: number;
}) => (
  <Card
    className={`animate-fade-in min-w-0 overflow-hidden ${accent ? "border-primary/30 bg-primary/5" : ""}`}
    style={{ animationDelay: `${animDelay}ms`, animationFillMode: "both" }}
  >
    <CardContent className="p-3.5">
      <div className="flex items-center gap-1.5 mb-1.5">
        <Icon className={`w-3.5 h-3.5 ${accent ? "text-primary animate-pulse" : "text-muted-foreground"}`} />
        <span className="min-w-0 truncate text-[11px] font-medium text-muted-foreground">{label}</span>
      </div>
      <p className={`truncate text-xl font-extrabold leading-none tracking-tight ${accent ? "text-primary" : "text-foreground"}`}>
        {value}
      </p>
      {sub && <div className="mt-1">{sub}</div>}
    </CardContent>
  </Card>
);

// ── Summary cards ──
const SummaryCards = ({ data }: { data: AnalyticsData }) => {
  const pctChange = (curr: number, prev: number) =>
    prev > 0 ? Math.round(((curr - prev) / prev) * 100) : curr > 0 ? 100 : 0;

  const visitorChange = pctChange(data.todayVisitors, data.yesterdayVisitors);
  const weekChange = pctChange(data.weekVisitors, data.lastWeekVisitors);
  const monthChange = pctChange(data.monthVisitors, data.lastMonthVisitors);

  const trendSub = (change: number, label: string) => {
    const Icon = change > 0 ? TrendingUp : change < 0 ? TrendingDown : Minus;
    const color = change > 0 ? "text-primary" : change < 0 ? "text-destructive" : "text-muted-foreground";
    return (
      <div className={`flex items-center gap-0.5 text-[10px] ${color}`}>
        <Icon className="w-2.5 h-2.5" />
        <span>{change > 0 ? "+" : ""}{change}% {label}</span>
      </div>
    );
  };

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      <StatCard icon={Radio} label="Online (30 min)" value={data.activeNow} accent animDelay={0} />
      <StatCard
        icon={Users}
        label="Dziś"
        value={data.todayVisitors}
        animDelay={60}
        sub={trendSub(visitorChange, "vs wczoraj")}
      />
      <StatCard icon={Eye} label="Odsłony" value={data.todayPageviews} animDelay={120} />
      <StatCard icon={Users} label="Wczoraj" value={data.yesterdayVisitors} animDelay={180} />
      <StatCard
        icon={Users}
        label="Ten tydzień"
        value={data.weekVisitors}
        animDelay={240}
        sub={trendSub(weekChange, `vs poprz. (${data.lastWeekVisitors})`)}
      />
      <StatCard
        icon={Users}
        label="Ten miesiąc"
        value={data.monthVisitors}
        animDelay={300}
        sub={trendSub(monthChange, `vs poprz. (${data.lastMonthVisitors})`)}
      />
      <StatCard icon={LogOut} label="Bounce rate" value={`${data.bounceRate}%`} animDelay={360} />
      <StatCard icon={Clock} label="Śr. sesja" value={formatDuration(data.avgSessionDuration)} animDelay={420} />
    </div>
  );
};

// ── Weekly chart with gradient area ──
const WeeklyChart = ({ data }: { data: AnalyticsData }) => (
  <Card className="animate-fade-in" style={{ animationDelay: "200ms", animationFillMode: "both" }}>
    <CardHeader className="pb-2">
      <CardTitle className="text-sm font-semibold">Ostatnie 7 dni</CardTitle>
    </CardHeader>
    <CardContent>
      <ResponsiveContainer width="100%" height={260}>
        <AreaChart data={data.weeklyData}>
          <defs>
            <linearGradient id="gradVisitors" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity={0.3} />
              <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity={0.02} />
            </linearGradient>
            <linearGradient id="gradPageviews" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity={0.15} />
              <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity={0.01} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" className="stroke-border" vertical={false} />
          <XAxis dataKey="date" className="text-xs" tickLine={false} axisLine={false} />
          <YAxis className="text-xs" tickLine={false} axisLine={false} />
          <Tooltip
            contentStyle={{
              backgroundColor: "hsl(var(--card))",
              border: "1px solid hsl(var(--border))",
              borderRadius: "8px",
              color: "hsl(var(--foreground))",
              fontSize: "12px",
            }}
          />
          <Legend wrapperStyle={{ fontSize: "12px" }} />
          <Area type="monotone" dataKey="visitors" name="Odwiedzający" fill="url(#gradVisitors)" stroke="hsl(var(--primary))" strokeWidth={2} dot={{ r: 3, fill: "hsl(var(--primary))" }} />
          <Area type="monotone" dataKey="pageviews" name="Odsłony" fill="url(#gradPageviews)" stroke="hsl(var(--primary) / 0.4)" strokeWidth={1.5} strokeDasharray="4 4" dot={false} />
        </AreaChart>
      </ResponsiveContainer>
    </CardContent>
  </Card>
);

// ── Weeks/months trend bar chart ──
const TrendBarChart = ({ title, items }: { title: string; items: { label: string; visitors: number }[] }) => (
  <Card>
    <CardHeader className="pb-2">
      <CardTitle className="text-sm font-semibold flex items-center gap-2">
        <TrendingUp className="w-3.5 h-3.5 text-muted-foreground" />
        {title}
      </CardTitle>
    </CardHeader>
    <CardContent>
      <ResponsiveContainer width="100%" height={220}>
        <BarChart data={items}>
          <CartesianGrid strokeDasharray="3 3" className="stroke-border" vertical={false} />
          <XAxis dataKey="label" className="text-xs" tickLine={false} axisLine={false} />
          <YAxis className="text-xs" tickLine={false} axisLine={false} allowDecimals={false} />
          <Tooltip
            contentStyle={{
              backgroundColor: "hsl(var(--card))",
              border: "1px solid hsl(var(--border))",
              borderRadius: "8px",
              color: "hsl(var(--foreground))",
              fontSize: "12px",
            }}
            formatter={(v: number) => [v, "Odwiedzający"]}
          />
          <Bar dataKey="visitors" fill="hsl(var(--primary))" radius={[6, 6, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </CardContent>
  </Card>
);

// ── Peak hours chart ──
const PeakHoursChart = ({ data }: { data: AnalyticsData }) => (
  <Card>
    <CardHeader className="pb-2">
      <CardTitle className="text-sm font-semibold flex items-center gap-2">
        <Clock className="w-3.5 h-3.5 text-muted-foreground" />
        Godziny szczytu (7 dni)
      </CardTitle>
    </CardHeader>
    <CardContent>
      <ResponsiveContainer width="100%" height={260}>
        <AreaChart data={data.peakHours}>
          <defs>
            <linearGradient id="gradHours" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity={0.25} />
              <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity={0.02} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" className="stroke-border" vertical={false} />
          <XAxis dataKey="hour" className="text-[10px]" interval={3} tickLine={false} axisLine={false} />
          <YAxis className="text-[10px]" tickLine={false} axisLine={false} />
          <Tooltip
            contentStyle={{
              backgroundColor: "hsl(var(--card))",
              border: "1px solid hsl(var(--border))",
              borderRadius: "8px",
              color: "hsl(var(--foreground))",
              fontSize: "12px",
            }}
          />
          <Area type="monotone" dataKey="pageviews" name="Odsłony" fill="url(#gradHours)" stroke="hsl(var(--primary))" strokeWidth={2} />
        </AreaChart>
      </ResponsiveContainer>
    </CardContent>
  </Card>
);


// ── Ranked list with progress bars ──
const RankedList = ({ title, icon: Icon, items, labelKey, countKey, scrollable, compact }: {
  title: string;
  icon: React.ElementType;
  items: Record<string, unknown>[];
  labelKey: string;
  countKey: string;
  scrollable?: boolean;
  compact?: boolean;
}) => {
  const maxCount = items.length > 0 ? Math.max(...items.map((item) => Number(item[countKey]) || 0)) : 1;

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-semibold flex items-center gap-2">
          <Icon className="w-3.5 h-3.5 text-muted-foreground" />
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className={`${compact ? "space-y-1 max-h-[120px]" : "space-y-2.5 max-h-[170px]"} overflow-y-auto pr-1`}>
          {items.length === 0 && <p className="text-xs text-muted-foreground py-2">Brak danych</p>}
          {items.map((item, i) => {
            const count = Number(item[countKey]) || 0;
            const pct = maxCount > 0 ? (count / maxCount) * 100 : 0;
            if (compact) {
              return (
                <div key={i} className="flex items-center gap-1 text-xs min-w-0">
                  <span className="text-muted-foreground tabular-nums shrink-0 w-5">{i + 1}.</span>
                  <span className="truncate text-foreground" title={String(item[labelKey])}>{String(item[labelKey])}</span>
                  {count > 1 && (
                    <span className="text-muted-foreground ml-auto shrink-0 tabular-nums">×{count}</span>
                  )}
                </div>
              );
            }
            return (
              <div key={i} className="space-y-1">
                <div className="flex items-center justify-between text-xs">
                  <span className="truncate flex-1 text-muted-foreground flex items-center gap-1 min-w-0">
                    <span className="text-foreground font-semibold mr-0.5 shrink-0">{i + 1}.</span>
                    {labelKey === "name" ? (
                      <ShelterNameTooltip name={String(item[labelKey])}><span className="truncate">{String(item[labelKey])}</span></ShelterNameTooltip>
                    ) : (
                      <span className="truncate" title={String(item[labelKey])}>{String(item[labelKey])}</span>
                    )}
                    {(item as any).verified === true && (
                      <BadgeCheck className="w-3 h-3 text-primary shrink-0" aria-label="Zweryfikowane" />
                    )}
                  </span>
                  <span className="text-foreground font-semibold ml-2 tabular-nums">{count}</span>
                </div>
                <div className="h-1.5 w-full bg-muted rounded-full overflow-hidden">
                  <div
                    className="h-full bg-primary/40 rounded-full transition-all duration-500"
                    style={{ width: `${pct}%` }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

const InlineQueryList = ({ title, items }: { title: string; items: { query: string; count: number }[] }) => (
  <Card>
    <CardHeader className="pb-2">
      <CardTitle className="text-sm font-semibold flex items-center gap-2">
        <Search className="w-3.5 h-3.5 text-muted-foreground" />
        {title}
      </CardTitle>
    </CardHeader>
    <CardContent>
      {items.length === 0 ? (
        <p className="text-xs text-muted-foreground py-2">Brak danych</p>
      ) : (
        <p className="text-xs text-muted-foreground leading-relaxed">
          {items.map((it, i) => (
            <span key={i}>
              <span className="text-foreground">{it.query}</span>
              {it.count > 1 && <span className="text-muted-foreground/70"> ×{it.count}</span>}
              {i < items.length - 1 && <span className="text-muted-foreground/50">, </span>}
            </span>
          ))}
        </p>
      )}
    </CardContent>
  </Card>
);

// ── Entry → Exit ──
const PAGE_LABELS: Record<string, string> = {
  "/": "Strona główna",
  "/zglos": "Zgłoś aktualizację",
  "/dla-schronisk": "Dla schronisk",
  "/polityka-prywatnosci": "Polityka prywatności",
  "/admin": "Panel admina",
  "/szybka-aktualizacja": "Szybka aktualizacja",
  "/404": "Nie znaleziono",
};
const friendlyPage = (path: string) => {
  if (PAGE_LABELS[path]) return PAGE_LABELS[path];
  if (path.startsWith("/zglos/")) return "Zgłoś aktualizację";
  if (path.startsWith("/schronisko/")) return path;
  return path;
};

// ── Entry → Exit body ──
const EntryExitBody = ({ items }: { items: { entry: string; exit: string; count: number }[] }) => {
  if (items.length === 0) return <p className="text-xs text-muted-foreground py-2">Brak danych</p>;
  const maxCount = Math.max(...items.map(e => e.count));
  return (
    <div className="space-y-2.5">
      {items.map((ep, i) => {
        const pct = maxCount > 0 ? (ep.count / maxCount) * 100 : 0;
        return (
          <div key={i} className="space-y-1">
            <div className="flex items-center justify-between text-xs">
              <span className="flex items-center gap-1.5 flex-1 min-w-0 text-muted-foreground">
                <span className="text-foreground font-semibold shrink-0">{i + 1}.</span>
                <span className="truncate" title={ep.entry}>{friendlyPage(ep.entry)}</span>
                <ArrowRight className="w-3 h-3 text-primary/40 shrink-0" />
                <span className="truncate" title={ep.exit}>{friendlyPage(ep.exit)}</span>
              </span>
              <span className="text-foreground font-semibold ml-2 tabular-nums shrink-0">{ep.count}</span>
            </div>
            <div className="h-1.5 w-full bg-muted rounded-full overflow-hidden">
              <div className="h-full bg-primary/40 rounded-full transition-all duration-500" style={{ width: `${pct}%` }} />
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ── Shelter sources body (stacked bar) ──
const ShelterSourcesBody = ({ items }: { items: { source: string; count: number }[] }) => {
  const total = items.reduce((sum, s) => sum + s.count, 0);
  if (total === 0) return <p className="text-xs text-muted-foreground py-2">Brak danych</p>;
  return (
    <div>
      <div className="flex items-center gap-3 mb-2">
        <div className="flex h-2.5 w-full rounded-full overflow-hidden bg-muted">
          {items.map((s, i) => {
            const pct = (s.count / total) * 100;
            return (
              <div
                key={i}
                className="h-full"
                style={{ width: `${pct}%`, background: SOURCE_COLORS[i % SOURCE_COLORS.length] }}
                title={`${s.source}: ${s.count} (${Math.round(pct)}%)`}
              />
            );
          })}
        </div>
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs">
        {items.map((s, i) => {
          const pct = Math.round((s.count / total) * 100);
          return (
            <div key={i} className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-sm shrink-0" style={{ background: SOURCE_COLORS[i % SOURCE_COLORS.length] }} />
              <span className="text-foreground">{s.source}</span>
              <span className="text-muted-foreground tabular-nums">{s.count} ({pct}%)</span>
            </div>
          );
        })}
      </div>
      <p className="text-[10px] text-muted-foreground mt-2">Łącznie: {total}</p>
    </div>
  );
};

// ── Forms body ──
const FormsBody = ({
  submitted,
  abandoned,
  byShelter,
  abandonedList,
}: {
  submitted: number;
  abandoned: number;
  byShelter: { name: string; opened: number; submitted: number }[];
  abandonedList: { name: string; time: string; type: string }[];
}) => (
  <div className="grid md:grid-cols-3 gap-6 [&>*]:min-w-0">
    <div className="space-y-2">
      <div className="flex items-baseline gap-2">
        <span className="text-2xl font-bold text-foreground tabular-nums">{submitted}</span>
        <span className="text-xs text-muted-foreground">wysłane</span>
      </div>
      <div className="flex items-baseline gap-2">
        <span className="text-lg font-semibold text-muted-foreground tabular-nums">{abandoned}</span>
        <span className="text-xs text-muted-foreground">porzucone</span>
      </div>
      {submitted + abandoned > 0 && (
        <>
          <div className="h-2 w-full bg-muted rounded-full overflow-hidden">
            <div
              className="h-full bg-primary transition-all"
              style={{ width: `${Math.round((submitted / (submitted + abandoned)) * 100)}%` }}
            />
          </div>
          <p className="text-[11px] text-muted-foreground">
            Konwersja: {Math.round((submitted / (submitted + abandoned)) * 100)}%
          </p>
        </>
      )}
    </div>
    <div className="space-y-2">
      <span className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider">Per schronisko</span>
      <div className="space-y-2 max-h-[180px] overflow-y-auto pr-1">
        {byShelter.length === 0 && <p className="text-xs text-muted-foreground">Brak danych</p>}
        {byShelter.map((s, i) => (
          <div key={i} className="space-y-0.5 min-w-0">
            <ShelterNameTooltip name={s.name}><p className="text-xs text-muted-foreground leading-snug truncate">{s.name}</p></ShelterNameTooltip>
            <div className="flex items-center gap-2 text-[11px] tabular-nums">
              <span className="text-primary">✓ {s.submitted}</span>
              <span className="text-muted-foreground/60">✗ {Math.max(0, s.opened - s.submitted)}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
    <div className="space-y-2">
      <span className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider">Porzucone - kiedy</span>
      <div className="space-y-1.5 max-h-[180px] overflow-y-auto pr-1">
        {abandonedList.length === 0 && <p className="text-xs text-muted-foreground">Brak danych</p>}
        {abandonedList.map((f, i) => (
          <div key={i} className="flex items-start justify-between gap-2 text-[11px]">
            <ShelterNameTooltip name={f.name}><span className="text-muted-foreground leading-snug truncate flex-1">{f.name}</span></ShelterNameTooltip>
            <span className="text-muted-foreground/70 shrink-0 tabular-nums">
              {new Date(f.time).toLocaleString("pl-PL", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}
            </span>
          </div>
        ))}
      </div>
    </div>
  </div>
);

// ── Tile that wraps a card title with tabs in the header ──
const TabbedCard = ({
  icon: Icon,
  title,
  tabs,
  defaultValue,
}: {
  icon: React.ElementType;
  title: string;
  tabs: { value: string; label: string; content: React.ReactNode }[];
  defaultValue?: string;
}) => (
  <Card className="min-w-0 overflow-hidden">
    <Tabs defaultValue={defaultValue ?? tabs[0].value}>
      <CardHeader className="pb-2">
        <div className="min-w-0 space-y-2 sm:flex sm:items-center sm:justify-between sm:gap-2 sm:space-y-0">
          <CardTitle className="flex min-w-0 items-center gap-2 text-sm font-semibold">
            <Icon className="w-3.5 h-3.5 text-muted-foreground" />
            <span className="min-w-0 truncate">{title}</span>
          </CardTitle>
          <div className="min-w-0">
          <TabsList className="grid h-auto w-full grid-cols-2 gap-0.5 p-0.5 sm:flex sm:h-7 sm:w-auto">
            {tabs.map((t) => (
              <TabsTrigger key={t.value} value={t.value} className="h-6 min-w-0 px-2 text-[11px] sm:flex-initial sm:px-3">
                {t.label}
              </TabsTrigger>
            ))}
          </TabsList>
          </div>
        </div>
      </CardHeader>
      <CardContent className="min-w-0">
        {tabs.map((t) => (
          <TabsContent key={t.value} value={t.value} className="mt-0">
            {t.content}
          </TabsContent>
        ))}
      </CardContent>
    </Tabs>
  </Card>
);

// ── Today shelter list (popular today with times) ──
const TodayShelterList = ({ items }: { items: AnalyticsData["todayPopularShelters"] }) =>
  items.length === 0 ? (
    <p className="text-xs text-muted-foreground py-2">Brak danych</p>
  ) : (
    <ul className={`min-w-0 space-y-2 text-xs ${items.length > 5 ? "max-h-[180px] overflow-y-auto pr-1" : ""}`}>
      {items.map((s, i) => (
        <li key={i} className="flex flex-col gap-0.5 min-w-0">
          <span className="flex items-center gap-1.5 font-medium text-foreground/90 min-w-0">
            <span className="text-muted-foreground/60 shrink-0">{i + 1}.</span>
            <ShelterNameTooltip name={s.name}><span className="min-w-0 flex-1 truncate">{s.name}</span></ShelterNameTooltip>
            {s.verified && <BadgeCheck className="w-3 h-3 text-primary shrink-0" />}
          </span>
          <span className="text-muted-foreground pl-4 tabular-nums truncate">{s.times.join(", ")}</span>
        </li>
      ))}
    </ul>
  );

// ── Recent shelter list ──
const RecentShelterList = ({ items }: { items: AnalyticsData["recentlyOpenedShelters"] }) =>
  !items || items.length === 0 ? (
    <p className="text-xs text-muted-foreground py-2">Brak danych</p>
  ) : (
    <div className="min-w-0 space-y-2 max-h-[180px] overflow-y-auto pr-1">
      {items.map((s, i) => (
        <div key={i} className="flex min-w-0 items-center justify-between gap-2 text-xs">
          <span className="truncate flex-1 text-muted-foreground flex items-center gap-1 min-w-0">
            <span className="text-foreground font-semibold mr-0.5 shrink-0">{i + 1}.</span>
            <ShelterNameTooltip name={s.name}><span className="min-w-0 flex-1 truncate">{s.name}</span></ShelterNameTooltip>
            {s.verified && <BadgeCheck className="w-3 h-3 text-primary shrink-0" />}
          </span>
          <span className="text-muted-foreground shrink-0 tabular-nums">
            {new Date(s.time).toLocaleString("pl-PL", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}
          </span>
        </div>
      ))}
    </div>
  );

// ── Today empty searches by visitor ──
const TodayEmptySearches = ({ items }: { items: AnalyticsData["todayEmptySearchesByVisitor"] }) =>
  (items?.length ?? 0) === 0 ? (
    <p className="text-xs text-muted-foreground py-2">Brak danych</p>
  ) : (
    <ul className={`space-y-2 text-xs ${(items?.length ?? 0) > 4 ? "max-h-[180px] overflow-y-auto pr-1" : ""}`}>
      {(items ?? []).map((v) => (
        <li key={v.label} className="flex gap-2">
          <span className="inline-flex items-center justify-center min-w-[20px] h-[20px] px-1 rounded bg-primary/15 text-primary text-[11px] font-bold shrink-0">
            {v.label}
          </span>
          <span className="text-foreground/90 leading-relaxed">
            {v.queries.map((q, i) => (
              <span key={i}>
                {q.query}
                {i < v.queries.length - 1 && <span className="text-muted-foreground/50">, </span>}
              </span>
            ))}
          </span>
        </li>
      ))}
    </ul>
  );

// ── Reusable simple ranked list body ──
const SimpleRankedBody = ({ items, labelKey, countKey }: { items: Record<string, unknown>[]; labelKey: string; countKey: string }) => {
  const maxCount = items.length > 0 ? Math.max(...items.map((i) => Number(i[countKey]) || 0)) : 1;
  if (items.length === 0) return <p className="text-xs text-muted-foreground py-2">Brak danych</p>;
  return (
    <div className="space-y-2.5 max-h-[180px] overflow-y-auto pr-1">
      {items.map((item, i) => {
        const count = Number(item[countKey]) || 0;
        const pct = maxCount > 0 ? (count / maxCount) * 100 : 0;
        return (
          <div key={i} className="space-y-1">
            <div className="flex items-center justify-between text-xs">
              <span className="truncate flex-1 text-muted-foreground flex items-center gap-1 min-w-0">
                <span className="text-foreground font-semibold mr-0.5 shrink-0">{i + 1}.</span>
                {labelKey === "name" ? (
                  <ShelterNameTooltip name={String(item[labelKey])}><span className="truncate">{String(item[labelKey])}</span></ShelterNameTooltip>
                ) : (
                  <span className="truncate" title={String(item[labelKey])}>{String(item[labelKey])}</span>
                )}
                {(item as any).verified === true && <BadgeCheck className="w-3 h-3 text-primary shrink-0" />}
              </span>
              <span className="text-foreground font-semibold ml-2 tabular-nums">{count}</span>
            </div>
            <div className="h-1.5 w-full bg-muted rounded-full overflow-hidden">
              <div className="h-full bg-primary/40 rounded-full transition-all duration-500" style={{ width: `${pct}%` }} />
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ── Chat stats body (categories + subcategories side by side) ──
const ChatStatsBody = ({ stats }: { stats: AnalyticsData["chatStats"] }) => {
  if (stats.categories.length === 0 && stats.subcategories.length === 0) {
    return <p className="text-xs text-muted-foreground py-2">Brak kliknięć</p>;
  }
  return (
    <div className="grid md:grid-cols-2 gap-4">
      <div>
        <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider mb-2">Kategorie</p>
        {stats.categories.length === 0 ? (
          <p className="text-xs text-muted-foreground">Brak</p>
        ) : (
          <div className="grid grid-cols-[1fr_auto] gap-x-3 gap-y-1.5 text-xs">
            {stats.categories.map((c) => (
              <div key={c.key} className="contents">
                <span className="text-muted-foreground truncate">{c.label}</span>
                <span className="text-foreground tabular-nums text-right font-semibold">{c.count}</span>
              </div>
            ))}
          </div>
        )}
      </div>
      <div>
        <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider mb-2">Podkategorie</p>
        {stats.subcategories.length === 0 ? (
          <p className="text-xs text-muted-foreground">Brak</p>
        ) : (
          <div className="grid grid-cols-[1fr_auto] gap-x-3 gap-y-1.5 text-xs max-h-[180px] overflow-y-auto pr-1">
            {stats.subcategories.map((s) => (
              <div key={s.subcategory} className="contents">
                <span className="text-muted-foreground truncate" title={s.subcategory}>{s.subcategory}</span>
                <span className="text-foreground tabular-nums text-right font-semibold">{s.count}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ── Trend bar with toggle ──
const TrendToggle = ({ data }: { data: AnalyticsData }) => {
  const [view, setView] = useState<"week" | "month">("week");
  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between gap-2">
          <CardTitle className="text-sm font-semibold flex items-center gap-2">
            <TrendingUp className="w-3.5 h-3.5 text-muted-foreground" />
            {view === "week" ? "Tygodnie" : "Miesiące"}
          </CardTitle>
          <Tabs value={view} onValueChange={(v) => setView(v as "week" | "month")}>
            <TabsList className="h-7 p-0.5">
              <TabsTrigger value="week" className="text-[11px] h-6 px-2">Tygodnie</TabsTrigger>
              <TabsTrigger value="month" className="text-[11px] h-6 px-2">Miesiące</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={200}>
          <BarChart data={view === "week" ? data.weeklyTrend : data.monthlyTrend}>
            <CartesianGrid strokeDasharray="3 3" className="stroke-border" vertical={false} />
            <XAxis dataKey="label" className="text-xs" tickLine={false} axisLine={false} />
            <YAxis className="text-xs" tickLine={false} axisLine={false} allowDecimals={false} />
            <Tooltip
              contentStyle={{
                backgroundColor: "hsl(var(--card))",
                border: "1px solid hsl(var(--border))",
                borderRadius: "8px",
                color: "hsl(var(--foreground))",
                fontSize: "12px",
              }}
              formatter={(v: number) => [v, "Odwiedzający"]}
            />
            <Bar dataKey="visitors" fill="hsl(var(--primary))" radius={[6, 6, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
};

// ── Compact period comparison row ──
const PeriodCompare = ({ data }: { data: AnalyticsData }) => {
  const pctChange = (curr: number, prev: number) =>
    prev > 0 ? Math.round(((curr - prev) / prev) * 100) : curr > 0 ? 100 : 0;
  const visitorChange = pctChange(data.todayVisitors, data.yesterdayVisitors);
  const weekChange = pctChange(data.weekVisitors, data.lastWeekVisitors);
  const monthChange = pctChange(data.monthVisitors, data.lastMonthVisitors);

  const Trend = ({ change }: { change: number }) => {
    const Icon = change > 0 ? TrendingUp : change < 0 ? TrendingDown : Minus;
    const color = change > 0 ? "text-primary" : change < 0 ? "text-destructive" : "text-muted-foreground";
    return (
      <span className={`inline-flex items-center gap-0.5 text-[10px] ${color}`}>
        <Icon className="w-2.5 h-2.5" />
        {change > 0 ? "+" : ""}{change}%
      </span>
    );
  };

  const Row = ({ label, value, change, sub }: { label: string; value: number | string; change?: number; sub?: string }) => (
    <div className="flex flex-col items-center gap-0.5 px-2">
      <span className="text-[10px] text-muted-foreground uppercase tracking-wider">{label}</span>
      <div className="flex items-center gap-1.5">
        <span className="text-sm font-bold text-foreground tabular-nums">{value}</span>
        {change !== undefined && <Trend change={change} />}
      </div>
      {sub && <span className="text-[10px] text-muted-foreground">{sub}</span>}
    </div>
  );

  return (
    <Card className="min-w-0 overflow-hidden">
      <CardContent className="p-3">
        <div className="grid min-w-0 grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-5">
          <Row label="Wczoraj" value={data.yesterdayVisitors} change={visitorChange} sub="vs dziś" />
          <Row label="Tydzień" value={data.weekVisitors} change={weekChange} sub={`poprz. ${data.lastWeekVisitors}`} />
          <Row label="Miesiąc" value={data.monthVisitors} change={monthChange} sub={`poprz. ${data.lastMonthVisitors}`} />
          <Row label="Bounce rate" value={`${data.bounceRate}%`} />
          <Row label="Śr. sesja" value={formatDuration(data.avgSessionDuration)} />
        </div>
      </CardContent>
    </Card>
  );
};

export const AnalyticsTab = () => {
  const { data, isLoading, error } = useAnalytics();
  const [botsOpen, setBotsOpen] = useState(false);

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-muted-foreground">
          <p>Nie udało się załadować analityki</p>
        </CardContent>
      </Card>
    );
  }

  const todayChatClicks =
    data.todayChatStats.categories.reduce((s, c) => s + c.count, 0) +
    data.todayChatStats.subcategories.reduce((s, c) => s + c.count, 0);

  return (
    <div className="min-w-0 space-y-4 overflow-x-hidden">
      <div className="flex min-w-0 items-center justify-between gap-3">
        <h2 className="font-display font-semibold text-lg">Analityka strony</h2>
        <span className="text-[10px] text-muted-foreground">Odświeża co minutę</span>
      </div>

      {/* ── Dziś ── */}
      <SectionHeader icon={CalendarDays} title="Dziś" />
      <div className="grid min-w-0 grid-cols-2 gap-3 md:grid-cols-4 [&>*]:min-w-0">
        <StatCard icon={Radio} label="Online (30 min)" value={data.activeNow} accent animDelay={0} />
        <StatCard icon={Users} label="Odwiedzający" value={data.todayVisitors} animDelay={60} />
        <StatCard icon={Eye} label="Odsłony" value={data.todayPageviews} animDelay={120} />
        <StatCard icon={Clock} label="Śr. czas sesji" value={formatDuration(data.todayAvgSessionDuration)} animDelay={180} />
      </div>

      <PeriodCompare data={data} />

      <div className="grid min-w-0 gap-4 md:grid-cols-2 [&>*]:min-w-0">
        <TabbedCard
          icon={Globe}
          title="Źródła ruchu"
          tabs={[
            { value: "today", label: "Dziś", content: <SimpleRankedBody items={data.todayReferrers} labelKey="referrer" countKey="count" /> },
            { value: "30d", label: "30 dni", content: <SimpleRankedBody items={data.topReferrers} labelKey="referrer" countKey="count" /> },
          ]}
        />
        <TabbedCard
          icon={Monitor}
          title="Urządzenia"
          tabs={[
            { value: "today", label: "Dziś", content: <SimpleRankedBody items={data.todayDeviceBreakdown} labelKey="device" countKey="count" /> },
            { value: "30d", label: "30 dni", content: <SimpleRankedBody items={data.deviceBreakdown} labelKey="device" countKey="count" /> },
          ]}
        />
      </div>

      <div className="grid min-w-0 gap-4 md:grid-cols-2 [&>*]:min-w-0">
        <TabbedCard
          icon={MapPin}
          title="Schroniska"
          tabs={[
            { value: "today", label: "Dziś", content: <TodayShelterList items={data.todayPopularShelters} /> },
            { value: "30d", label: "30 dni", content: <SimpleRankedBody items={data.popularShelters} labelKey="name" countKey="count" /> },
            { value: "recent", label: "Ostatnio", content: <RecentShelterList items={data.recentlyOpenedShelters || []} /> },
            { value: "least", label: "Najmniej", content: <SimpleRankedBody items={data.leastPopularShelters || []} labelKey="name" countKey="count" /> },
          ]}
        />
        <TabbedCard
          icon={Search}
          title="Szukane bez wyników"
          tabs={[
            { value: "today", label: "Dziś", content: <TodayEmptySearches items={data.todayEmptySearchesByVisitor} /> },
            {
              value: "30d",
              label: "30 dni",
              content: <TodayEmptySearches items={data.emptySearchesByVisitor} />,
            },
          ]}
        />
      </div>

      <TabbedCard
        icon={FileText}
        title="Otwierane podstrony"
        tabs={[
          {
            value: "today",
            label: "Dziś",
            content: (
              <div className="grid min-w-0 grid-cols-[minmax(0,1fr)_auto_auto] gap-x-2 gap-y-2 text-xs sm:gap-x-4">
                <span className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider">Strona</span>
                <span className="text-right text-[10px] font-medium uppercase tracking-wider text-muted-foreground sm:hidden">Unik.</span>
                <span className="hidden text-right text-[10px] font-medium uppercase tracking-wider text-muted-foreground sm:block">Unikalni</span>
                <span className="text-right text-[10px] font-medium uppercase tracking-wider text-muted-foreground">Odsł.</span>
                {data.todayKeyPages.map((p) => (
                  <div key={p.label} className="contents">
                    <span className="text-muted-foreground truncate">{p.label}</span>
                    <span className={`tabular-nums text-right font-semibold ${p.visitors > 0 ? "text-foreground" : "text-muted-foreground/50"}`}>{p.visitors}</span>
                    <span className={`tabular-nums text-right ${p.pageviews > 0 ? "text-foreground" : "text-muted-foreground/50"}`}>{p.pageviews}</span>
                  </div>
                ))}
              </div>
            ),
          },
          { value: "30d", label: "30 dni", content: <SimpleRankedBody items={data.topPages} labelKey="path" countKey="count" /> },
        ]}
      />

      <TabbedCard
        icon={MessageCircle}
        title="Czat pomocy"
        tabs={[
          {
            value: "today",
            label: `Dziś (${data.todayChatStats.opens})`,
            content: (
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <StatCard icon={MessageCircle} label="Otwarcia" value={data.todayChatStats.opens} animDelay={0} />
                  <StatCard icon={MousePointerClick} label="Kliknięcia" value={todayChatClicks} animDelay={60} />
                </div>
                <ChatStatsBody stats={data.todayChatStats} />
              </div>
            ),
          },
          {
            value: "30d",
            label: "30 dni",
            content: (
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <StatCard icon={MessageCircle} label="Otwarcia" value={data.chatStats.opens} animDelay={0} />
                  <StatCard icon={Send} label="Wysłane" value={data.chatStats.submits} animDelay={60} />
                </div>
                <ChatStatsBody stats={data.chatStats} />
              </div>
            ),
          },
        ]}
      />

      {/* ── Trendy ── */}
      <SectionHeader icon={TrendingUp} title="Trendy" />
      <WeeklyChart data={data} />
      <div className="grid min-w-0 gap-4 md:grid-cols-2 [&>*]:min-w-0">
        <TrendToggle data={data} />
        <PeakHoursChart data={data} />
      </div>

      {/* ── Konwersje ── */}
      <SectionHeader icon={Phone} title="Konwersje" />
      <div className="grid min-w-0 gap-4 md:grid-cols-3 [&>*]:min-w-0">
        <TabbedCard
          icon={Phone}
          title="Kontakt"
          tabs={[
            { value: "today", label: "Dziś", content: <SimpleRankedBody items={data.todayContactClicks || []} labelKey="type" countKey="count" /> },
            { value: "30d", label: "30 dni", content: <SimpleRankedBody items={data.contactClicks || []} labelKey="type" countKey="count" /> },
          ]}
        />
        <TabbedCard
          icon={MousePointerClick}
          title="Przyciski"
          tabs={[
            { value: "today", label: "Dziś", content: <SimpleRankedBody items={data.todayButtonClicks || []} labelKey="name" countKey="count" /> },
            { value: "30d", label: "30 dni", content: <SimpleRankedBody items={data.buttonClicks} labelKey="name" countKey="count" /> },
          ]}
        />
        <TabbedCard
          icon={MapPin}
          title="Mapa miast"
          tabs={[
            { value: "today", label: "Dziś", content: <SimpleRankedBody items={data.todayCityHeatmap || []} labelKey="city" countKey="count" /> },
            { value: "30d", label: "30 dni", content: <SimpleRankedBody items={data.cityHeatmap} labelKey="city" countKey="count" /> },
          ]}
        />
      </div>

      <div className="grid min-w-0 gap-4 md:grid-cols-2 [&>*]:min-w-0">
        <TabbedCard
          icon={Navigation}
          title="Źródło otwarć schronisk"
          tabs={[
            { value: "today", label: "Dziś", content: <ShelterSourcesBody items={data.todayShelterOpenSources || []} /> },
            { value: "30d", label: "30 dni", content: <ShelterSourcesBody items={data.shelterOpenSources} /> },
          ]}
        />
        <TabbedCard
          icon={ArrowRight}
          title="Wejście → Wyjście"
          tabs={[
            { value: "today", label: "Dziś", content: <EntryExitBody items={data.todayEntryExitPages || []} /> },
            { value: "30d", label: "30 dni", content: <EntryExitBody items={data.entryExitPages} /> },
          ]}
        />
      </div>

      <TabbedCard
        icon={FileX}
        title="Formularze"
        tabs={[
          {
            value: "today",
            label: "Dziś",
            content: (
              <FormsBody
                submitted={data.todaySubmittedForms ?? 0}
                abandoned={data.todayAbandonedForms ?? 0}
                byShelter={data.todayFormsByShelter || []}
                abandonedList={data.todayAbandonedFormsList || []}
              />
            ),
          },
          {
            value: "30d",
            label: "30 dni",
            content: (
              <FormsBody
                submitted={data.submittedForms ?? 0}
                abandoned={data.abandonedForms ?? 0}
                byShelter={data.formsByShelter || []}
                abandonedList={data.abandonedFormsList || []}
              />
            ),
          },
        ]}
      />

      {/* ── Udostępnienia 30 dni ── */}
      <SectionHeader icon={Share2} title="Udostępnienia (30 dni)" />
      <div className="grid min-w-0 grid-cols-2 gap-3 [&>*]:min-w-0">
        <StatCard icon={Link2} label="Skopiowane linki" value={data.shareStats.totalCopies} accent animDelay={0} />
        <StatCard icon={Users} label="Wejścia z linku" value={data.shareStats.totalShareVisits} animDelay={60} />
      </div>
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold flex items-center gap-2">
            <Share2 className="w-3.5 h-3.5 text-muted-foreground" />
            Top schroniska - udostępnienia
          </CardTitle>
        </CardHeader>
      <CardContent className="min-w-0 overflow-hidden">
          {data.shareStats.perShelter.length === 0 ? (
            <p className="text-xs text-muted-foreground py-2">Brak udostępnień w ostatnich 30 dniach</p>
          ) : (
            <>
              <div className="grid min-w-0 grid-cols-[1.25rem_minmax(0,1fr)_auto_auto] gap-x-2 gap-y-2 border-b border-border pb-2 text-[10px] font-medium uppercase tracking-wider text-muted-foreground sm:grid-cols-[1.5rem_minmax(0,1fr)_auto_auto_auto] sm:gap-x-3 sm:text-[11px]">
                <span></span>
                <span>Schronisko</span>
                <span className="text-right">Skopiowane</span>
                <span className="text-right">Wejścia</span>
                <span className="hidden text-right sm:block">CTR</span>
              </div>
              <div className="grid min-w-0 grid-cols-[1.25rem_minmax(0,1fr)_auto_auto] gap-x-2 gap-y-2 pt-2 text-xs sm:grid-cols-[1.5rem_minmax(0,1fr)_auto_auto_auto] sm:gap-x-3">
                {data.shareStats.perShelter.map((s, i) => {
                  const ctr = s.copies > 0 ? Math.round((s.visits / s.copies) * 100) : 0;
                  return (
                    <div key={s.shelter_id || i} className="contents">
                      <span className="text-foreground font-semibold tabular-nums">{i + 1}.</span>
                      <ShelterNameTooltip name={s.name}>
                        <span className="text-muted-foreground truncate min-w-0">
                          {s.name}{s.city && <span className="text-muted-foreground/60"> · {s.city}</span>}
                        </span>
                      </ShelterNameTooltip>
                      <span className="text-foreground tabular-nums text-right">{s.copies}</span>
                      <span className="text-foreground tabular-nums text-right">{s.visits}</span>
                      <span className="hidden text-right tabular-nums text-muted-foreground sm:block">{s.copies > 0 ? `${ctr}%` : "-"}</span>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* ── Wykluczony ruch (collapsible) ── */}
      <Collapsible open={botsOpen} onOpenChange={setBotsOpen}>
        <Card>
          <CollapsibleTrigger asChild>
            <button className="w-full flex items-center justify-between p-4 text-left hover:bg-muted/40 transition-colors rounded-lg">
              <div className="flex items-center gap-2">
                <Bot className="w-4 h-4 text-muted-foreground" />
                <span className="text-sm font-semibold">Wykluczony ruch (boty)</span>
                <Badge variant="secondary" className="text-[10px]">
                  Dziś: {data.botStats.today} · 7 dni: {data.botStats.week}
                </Badge>
              </div>
              <ChevronDown className={`w-4 h-4 text-muted-foreground transition-transform ${botsOpen ? "rotate-180" : ""}`} />
            </button>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <CardContent className="pt-0">
              {data.botStats.byPattern.length === 0 ? (
                <p className="text-xs text-muted-foreground py-2">Brak wykluczonego ruchu w ostatnich 7 dniach</p>
              ) : (
                <>
                  <div className="grid grid-cols-[1fr_auto_auto] gap-x-3 gap-y-2 text-[11px] font-medium text-muted-foreground uppercase tracking-wider pb-2 border-b border-border">
                    <span>Wzorzec</span>
                    <span className="text-right">Dziś</span>
                    <span className="text-right">7 dni</span>
                  </div>
                  <div className="grid grid-cols-[1fr_auto_auto] gap-x-3 gap-y-2 text-xs pt-2">
                    {data.botStats.byPattern.map((p) => (
                      <div key={p.pattern} className="contents">
                        <span className="text-muted-foreground truncate min-w-0">{p.pattern}</span>
                        <span className="text-foreground tabular-nums text-right">{p.today}</span>
                        <span className="text-foreground tabular-nums text-right">{p.week}</span>
                      </div>
                    ))}
                  </div>
                  <p className="text-[11px] text-muted-foreground pt-3 border-t border-border mt-3">
                    Te wizyty są zapisywane w bazie, ale wykluczane z liczników odwiedzin.
                  </p>
                </>
              )}
            </CardContent>
          </CollapsibleContent>
        </Card>
      </Collapsible>
    </div>
  );
};
